import * as z from "zod";
import {
  Field,
  FieldContent,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Controller, type UseFormReturn } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { themeFormSchema } from "./schemas";
import type { ApiTheme } from "../../types";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ColorPicker } from "@/components/ui/color-picker";
import { useNavigate } from "react-router-dom";
import BottomFixed from "@/components/bottom-fixed";
interface ThemeFormProps {
  form: UseFormReturn<z.infer<typeof themeFormSchema>>;
  onSubmit: (data: z.infer<typeof themeFormSchema>) => void;
  isLoading: boolean;
  theme?: ApiTheme;
}

const ThemeForm = ({ form, onSubmit, isLoading, theme }: ThemeFormProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="p-4 rounded-md border w-full flex flex-col gap-y-4 bg-white">
      <form id="theme-form" onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup className="gap-y-5 grid grid-cols-2 w-full">
          <div className="col-span-2 flex sm:flex-row flex-col gap-5">
            <Controller
              name="name"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid} className="flex-1">
                  <FieldLabel htmlFor="name-input">
                    {t("modules.themes.keys.name")}{" "}
                    <span className="text-destructive">*</span>
                  </FieldLabel>
                  <Input
                    {...field}
                    id="name-input"
                    aria-invalid={fieldState.invalid}
                  />
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
            <Controller
              name="color"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid} className="flex-1">
                  <FieldLabel htmlFor="color-input">
                    {t("modules.themes.keys.color")}
                  </FieldLabel>
                  <ColorPicker
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
          </div>
          <Controller
            name="description"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field className="col-span-2" data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="description-input">
                  {t("modules.themes.keys.description")}
                </FieldLabel>
                <Textarea
                  {...field}
                  id="description-input"
                  className="min-h-32"
                  aria-invalid={fieldState.invalid}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
          <Controller
            name="status"
            control={form.control}
            render={({ field }) => (
              <Field orientation="vertical" className="col-span-2">
                <FieldContent>
                  <FieldLabel htmlFor="theme-status">
                    {t("modules.themes.keys.status")}
                  </FieldLabel>
                </FieldContent>
                <Switch
                  checked={field.value === "active"}
                  onCheckedChange={(checked) =>
                    field.onChange(checked ? "active" : "inactive")
                  }
                  id="theme-status"
                />
              </Field>
            )}
          />
        </FieldGroup>
      </form>
      <BottomFixed className="bg-white border-t border-l">
        <div className="flex flex-wrap flex-row items-center sm:justify-end gap-2">
          {theme && (
            <Button
              className="flex-1 sm:flex-none"
              variant="secondary"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft />
              {t("keys.cancel")}
            </Button>
          )}
          <Button
            className="flex-1 sm:flex-none"
            disabled={isLoading}
            type="submit"
            form="theme-form"
          >
            {isLoading ? <Loader className="animate-spin" /> : <Save />}
            {t("keys.save")}
          </Button>
        </div>
      </BottomFixed>
    </div>
  );
};

export default ThemeForm;
